import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Button,
  Alert,
  Box,
  IconButton,
  Stack,
  Typography,
  Divider,
  Chip,
  Modal as MuiModal,
  Backdrop,
  CardHeader,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { GridColDef } from "@mui/x-data-grid";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import DownloadIcon from "@mui/icons-material/Download";

import DataGrid from "../../../components/DataGrid";

import useNavStore from "../../../stores/nav";
import useAuthStore from "../../../stores/auth";
import { formatDate } from "../../../utils";
import { fetchData } from "../../../fetchData";

function Contabil() {
  const navigate = useNavigate();
  const setHeaderTitle = useNavStore((state: any) => state.setHeader);
  const setToolbar = useNavStore((state: any) => state.setToolbar);
  const setSnackbar = useNavStore((state: any) => state.setSnackbar);
  const company = useAuthStore((state) => state.company);
  const [progress, setProgress] = useState(false);
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const Toolbar = () => {
    return (
      <>
        <Box />
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => {
            navigate("/contabil/new-period");
          }}
        >
          Enviar novo período
        </Button>
      </>
    );
  };

  const formatPeriod = (period: string) => {
    if (!period) return "-";
    const [year, month] = period.split("-");
    return `${month}/${year}`;
  };

  const handleDownloadFile = (key: string, fileName: string) => {
    fetchData(
      `/cloud/download?key=${encodeURIComponent(key)}`,
      { method: "GET" },
      false,
    )
      .then((response: Response) => response.blob())
      .then((blob) => URL.createObjectURL(blob))
      .then((url) => {
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName || "arquivo";
        document.body.appendChild(link);
        link.dispatchEvent(
          new MouseEvent("click", {
            bubbles: true,
            cancelable: true,
            view: window,
          }),
        );
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      })
      .catch(() => {
        setSnackbar("Não foi possível baixar o arquivo.", "error");
      });
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "queued":
        return "Na fila";
      case "pending":
        return "Pendente";
      case "processing":
        return "Processando";
      case "done":
        return "Concluído";
      default:
        return status || "-";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "queued":
        return "info";
      case "pending":
        return "error";
      case "processing":
        return "warning";
      case "done":
        return "success";
      default:
        return "default";
    }
  };

  const getHistoryStatusLabel = (status: string) => {
    switch (status) {
      case "draft.created":
        return "Na fila";
      case "draft.pending":
        return "Pendente";
      case "draft.pending.sended":
        return "Na fila";
      case "draft.processing":
        return "Processando";
      case "draft.done":
        return "Concluído";
      default:
        return (status || "").toUpperCase();
    }
  };

  const getHistoryStatusColor = (status: string) => {
    switch (status) {
      case "draft.created":
      case "draft.pending.sended":
        return getStatusColor("queued");
      case "draft.pending":
        return getStatusColor("pending");
      case "draft.processing":
        return getStatusColor("processing");
      case "draft.done":
        return getStatusColor("done");
      default:
        return getStatusColor("");
    }
  };

  const getHistoryStatusDescription = (status: string) => {
    switch (status) {
      case "draft.created":
        return "Período contábil criado e enviado para a fila de análise.";
      case "draft.pending":
        return "Período movido para 'pendente' com inconsistências identificadas pelo nosso time.";
      case "draft.pending.sended":
        return "Pendências respondidas. Período reenviado para a fila de análise.";
      case "draft.processing":
        return "Período direcionado para processamento.";
      case "draft.done":
        return "Período contábil entregue.";
      default:
        return "";
    }
  };

  const columns: GridColDef[] = [
    {
      field: "period",
      headerName: "Período",
      flex: 1,
      renderCell: (params: any) => {
        return formatPeriod(params.row.period);
      },
    },
    {
      field: "registered_at",
      headerName: "Enviado em",
      flex: 1,
      renderCell: (params: any) => {
        return params.row.registered_at
          ? formatDate(params.row.registered_at, "D/M/Y")
          : "-";
      },
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params: any) => {
        return (
          <Chip
            label={getStatusLabel(params.row.status)}
            color={getStatusColor(params.row.status) as any}
            size="small"
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Ações",
      width: 260,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => {
        const row = params.row;
        const isPending = row.status === "pending";

        return (
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<VisibilityIcon />}
              onClick={() => {
                setSelectedRow(row);
                setDialogOpen(true);
              }}
            >
              Detalhes
            </Button>
            {isPending && (
              <Button
                variant="contained"
                color="error"
                size="small"
                startIcon={<ReportProblemIcon />}
                sx={(theme) => ({
                  "@keyframes pendingPulse": {
                    "0%, 100%": {
                      boxShadow: `0 0 0 0 ${alpha(
                        theme.palette.error.main,
                        0.5,
                      )}`,
                    },
                    "50%": {
                      boxShadow: `0 0 0 8px ${alpha(
                        theme.palette.error.main,
                        0,
                      )}`,
                    },
                  },
                  animation: "pendingPulse 1.5s ease-in-out infinite",
                })}
                onClick={() => {
                  navigate(`/contabil/pending/${row.id}`);
                }}
              >
                Pendências
              </Button>
            )}
          </Stack>
        );
      },
    },
  ];

  const data = async (limit: number, skip: number) => {
    if (!company?._id) {
      setProgress(false);
      return;
    }

    setProgress(true);

    try {
      const response = await fetchData(
        `/accounting/drafts?limit=${limit}&skip=${skip}&company=${company._id}`,
        {
          method: "GET",
        },
      );

      if (response) {
        const mappedRows = response.rows.map((row: any) => ({
          ...row,
          id: row._id,
        }));

        setRowCount(response.count);
        setRows(mappedRows);
      }
    } catch (error) {
      console.error("Erro ao carregar períodos:", error);
      setRowCount(0);
      setRows([]);
    } finally {
      setProgress(false);
    }
  };

  const [historyEvents, setHistoryEvents] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    setHeaderTitle("Movimentos contábeis");
    setToolbar(<Toolbar />);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadHistory = async () => {
      if (!dialogOpen || !selectedRow || !company?.reference) {
        setHistoryEvents([]);
        return;
      }

      try {
        setHistoryLoading(true);
        const response = await fetchData(
          `/organizations/${company.reference}/accounting-history?limit=100&skip=0`,
          { method: "GET" },
        );
        const allEvents = Array.isArray(response) ? response : [];
        const period = selectedRow.period;
        const filtered = allEvents.filter((item: any) => {
          const itemPeriod = item?.data?.period;
          const itemType = item?.type;
          const itemTypeId = item?.type_id;
          return (
            itemType === "accounting.draft" &&
            itemTypeId === selectedRow.id &&
            itemPeriod === period
          );
        });

        filtered.sort(
          (a: any, b: any) =>
            new Date(a.registered_at).getTime() -
            new Date(b.registered_at).getTime(),
        );

        setHistoryEvents(filtered);
      } catch (error) {
        console.error("Erro ao carregar histórico contábil:", error);
        setHistoryEvents([]);
      } finally {
        setHistoryLoading(false);
      }
    };

    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen, selectedRow, company?.reference]);

  const noRowsOverlay = (
    <Alert severity="info" sx={{ m: 2 }}>
      Nenhum período encontrado.
    </Alert>
  );

  return (
    <>
      <Card>
        <DataGrid
          rows={rows}
          data={data}
          loading={progress}
          columns={columns}
          rowCount={rowCount}
          noRowsOverlay={noRowsOverlay}
        />
      </Card>

      <MuiModal
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setSelectedRow(null);
        }}
        BackdropComponent={Backdrop}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.7)",
          },
        }}
      >
        <Card
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: "90%", md: 600 },
            maxWidth: 600,
            maxHeight: { xs: "90vh", md: "85vh" },
            p: 0,
            zIndex: 1301,
            overflow: "auto",
          }}
        >
          <CardHeader
            sx={{ borderBottom: "1px solid #CCCC" }}
            title={
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="h6" component="span" fontWeight={600}>
                  Detalhes do Período
                </Typography>
                <IconButton
                  aria-label="fechar"
                  size="small"
                  onClick={() => {
                    setDialogOpen(false);
                    setSelectedRow(null);
                  }}
                  edge="end"
                >
                  <CloseIcon />
                </IconButton>
              </Stack>
            }
          />
          <CardContent sx={{ p: 2 }}>
            {selectedRow && (
              <Stack spacing={2}>
                <Stack spacing={2} divider={<Divider />}>
                  <Stack direction="row">
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      sx={{ flex: 1 }}
                    >
                      Período
                    </Typography>
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {selectedRow.period
                        ? formatPeriod(selectedRow.period)
                        : "-"}
                    </Typography>
                  </Stack>

                  <Stack direction="row">
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      sx={{ flex: 1 }}
                    >
                      Status
                    </Typography>
                    <Box sx={{ flex: 1 }}>
                      <Chip
                        label={getStatusLabel(selectedRow.status)}
                        color={getStatusColor(selectedRow.status) as any}
                        size="small"
                      />
                    </Box>
                  </Stack>

                  <Stack direction="row">
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      sx={{ flex: 1 }}
                    >
                      ERP
                    </Typography>
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {selectedRow.erp === "null" || selectedRow.erp === null
                        ? "Não possui ERP"
                        : selectedRow.erp}
                    </Typography>
                  </Stack>

                  <Stack direction="row">
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      sx={{ flex: 1 }}
                    >
                      Enviado em
                    </Typography>
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {selectedRow.registered_at
                        ? new Date(selectedRow.registered_at).toLocaleString(
                            "pt-BR",
                            {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            },
                          )
                        : "-"}
                    </Typography>
                  </Stack>
                </Stack>

                <Divider />

                <Typography variant="body1" fontWeight="bold">
                  Arquivos Enviados
                </Typography>

                <Stack spacing={2}>
                  {selectedRow.files?.extrato?.length > 0 && (
                    <Stack spacing={1}>
                      <Typography variant="body2" fontWeight="bold">
                        Extratos ({selectedRow.files.extrato.length})
                      </Typography>
                      {selectedRow.files.extrato.map(
                        (file: any, index: number) => {
                          const fileName =
                            file.fileName.length > 50
                              ? `${file.fileName.substring(0, 50)}...`
                              : file.fileName;
                          return (
                            <Stack
                              key={index}
                              direction="row"
                              alignItems="center"
                              spacing={0.5}
                              sx={{ pl: 2 }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                title={file.fileName}
                              >
                                • {fileName}
                              </Typography>
                              {file.key && (
                                <IconButton
                                  size="small"
                                  aria-label="Baixar arquivo"
                                  onClick={() =>
                                    handleDownloadFile(
                                      file.key,
                                      file.fileName || "extrato",
                                    )
                                  }
                                  sx={{ p: 0.25 }}
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Stack>
                          );
                        },
                      )}
                    </Stack>
                  )}

                  {selectedRow.files?.relatorioFinanc?.length > 0 && (
                    <Stack spacing={1}>
                      <Typography variant="body2" fontWeight="bold">
                        Relatórios Financeiros (
                        {selectedRow.files.relatorioFinanc.length})
                      </Typography>
                      {selectedRow.files.relatorioFinanc.map(
                        (file: any, index: number) => {
                          const fileName =
                            file.fileName.length > 50
                              ? `${file.fileName.substring(0, 50)}...`
                              : file.fileName;
                          return (
                            <Stack
                              key={index}
                              direction="row"
                              alignItems="center"
                              spacing={0.5}
                              sx={{ pl: 2 }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                title={file.fileName}
                              >
                                • {fileName}
                              </Typography>
                              {file.key && (
                                <IconButton
                                  size="small"
                                  aria-label="Baixar arquivo"
                                  onClick={() =>
                                    handleDownloadFile(
                                      file.key,
                                      file.fileName || "relatorio",
                                    )
                                  }
                                  sx={{ p: 0.25 }}
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Stack>
                          );
                        },
                      )}
                    </Stack>
                  )}

                  {selectedRow.files?.docs?.length > 0 && (
                    <Stack spacing={1}>
                      <Typography variant="body2" fontWeight="bold">
                        Documentos ({selectedRow.files.docs.length})
                      </Typography>
                      {selectedRow.files.docs.map(
                        (file: any, index: number) => {
                          const fileName =
                            file.fileName.length > 50
                              ? `${file.fileName.substring(0, 50)}...`
                              : file.fileName;
                          return (
                            <Stack
                              key={index}
                              direction="row"
                              alignItems="center"
                              spacing={0.5}
                              sx={{ pl: 2 }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                title={file.fileName}
                              >
                                • {fileName}
                              </Typography>
                              {file.key && (
                                <IconButton
                                  size="small"
                                  aria-label="Baixar arquivo"
                                  onClick={() =>
                                    handleDownloadFile(
                                      file.key,
                                      file.fileName || "documento",
                                    )
                                  }
                                  sx={{ p: 0.25 }}
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Stack>
                          );
                        },
                      )}
                    </Stack>
                  )}

                  {!selectedRow.files?.extrato?.length &&
                    !selectedRow.files?.relatorioFinanc?.length &&
                    !selectedRow.files?.docs?.length && (
                      <Typography variant="caption" color="text.secondary">
                        Nenhum arquivo encontrado
                      </Typography>
                    )}
                </Stack>

                {historyLoading && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Carregando histórico...
                    </Typography>
                  </Box>
                )}

                {!historyLoading && historyEvents.length > 0 && (
                  <>
                    <Divider />
                    <Typography variant="body1" fontWeight="bold">
                      Histórico do Período
                    </Typography>
                    <Table size="small" sx={{ mt: 1 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell variant="head">Data</TableCell>
                          <TableCell variant="head">Descrição</TableCell>
                          <TableCell variant="head">Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {historyEvents.map((event: any, index: number) => {
                          const status = event?.data?.status || "";
                          const label = getHistoryStatusLabel(status);
                          const color = getHistoryStatusColor(status);
                          const description =
                            getHistoryStatusDescription(status);

                          return (
                            <TableRow key={event._id || index}>
                              <TableCell>
                                {formatDate(event.registered_at, "D/M/Y H:m")}
                              </TableCell>
                              <TableCell>{description || "-"}</TableCell>
                              <TableCell>
                                <Chip
                                  label={label}
                                  color={color as any}
                                  size="small"
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </>
                )}
              </Stack>
            )}
          </CardContent>
        </Card>
      </MuiModal>
    </>
  );
}

export default Contabil;
